/**
 * `NewsFeed` — vertical infinite-scroll feed of latest curated news items.
 *
 * Rendered in the right column on desktop (sticky), full-width on mobile.
 * Backed by `news.list({ slug, cursor, limit: 20 })` via TanStack Query
 * `useInfiniteQuery`. An IntersectionObserver sentinel at the bottom
 * triggers `fetchNextPage()` when it enters the viewport.
 *
 * SSR safety: TanStack Query and IntersectionObserver are client-only.
 * We render a stable empty shell during SSR (skeleton + sentinel) and
 * the query hydrates on the client. The `initialItems` prop allows the
 * SSR caller to seed the first page from `news.list` so the feed paints
 * with content on first byte.
 *
 * Severity colour comes from `getSeverityToken()`; small status dots
 * (rounded-full) are explicitly allowed by `.claude/rules/uncodixify-ui.md`.
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import type { NewsItem, NewsListOutput } from "@hantavirus/shared";
import { type Source, SourceCitation } from "#/components/shared/SourceCitation.js";
import { type TranslateFn, formatDate } from "#/lib/i18n.js";

export type NewsFeedProps = {
  /** Optional outbreak filter — omit for site-wide feed. */
  slug?: string;
  /** Which public news procedure backs the feed. */
  feedMode?: "curated" | "verified";
  /** UI locale, propagated as `x-locale` header to the API. */
  locale: string;
  /** Locale-resolved labels. Provided by parent page. */
  t: TranslateFn;
  /** Page size; defaults to 20 to match the router default. */
  limit?: number;
  /** Optional first page from SSR — keeps first paint flicker-free. */
  initialItems?: NewsListOutput;
  /** Optional override for the API base URL — used by tests. */
  apiBaseUrl?: string;
};

type NewsSeverity = NewsItem["severity"];

const SEVERITY_TOKEN: Record<NewsSeverity, string> = {
  urgent: "var(--color-accent-emergency)",
  warning: "var(--color-accent-warning)",
  info: "var(--color-accent-info)",
};

const RTF_DIVISIONS: Array<{ amount: number; unit: Intl.RelativeTimeFormatUnit }> = [
  { amount: 60, unit: "second" },
  { amount: 60, unit: "minute" },
  { amount: 24, unit: "hour" },
  { amount: 7, unit: "day" },
  { amount: 4.34524, unit: "week" },
  { amount: 12, unit: "month" },
  { amount: Number.POSITIVE_INFINITY, unit: "year" },
];

/**
 * Convert a Date to a locale-aware "2h ago"-style string. Pure helper —
 * exported for tests.
 */
export function formatRelative(
  value: string | Date,
  locale: string,
  now: Date = new Date(),
): string {
  const target = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(target.getTime())) return "";
  let duration = (target.getTime() - now.getTime()) / 1000;
  let rtf: Intl.RelativeTimeFormat;
  try {
    rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  } catch {
    rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  }
  for (const div of RTF_DIVISIONS) {
    if (Math.abs(duration) < div.amount) {
      return rtf.format(Math.round(duration), div.unit);
    }
    duration /= div.amount;
  }
  return rtf.format(Math.round(duration), "year");
}

/** Truncate plain text to N lines via CSS — pure DOM helper. */
function clampStyle(lines: number): React.CSSProperties {
  return {
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
}

type VerifiedNewsItem = Omit<NewsItem, "outbreakId">;

function buildApiUrl(base: string | undefined, procedure: string): string {
  const fallback =
    typeof import.meta !== "undefined" && typeof import.meta.env !== "undefined"
      ? (import.meta.env.VITE_API_URL as string | undefined)
      : undefined;
  const resolved = (base ?? fallback ?? "").replace(/\/+$/u, "");
  return `${resolved}/api/trpc/${procedure}`;
}

/**
 * Plain `fetch` against the public tRPC HTTP transport. We don't pull in
 * the `@trpc/client` runtime because the news feed has a single
 * procedure; the cost (a few KB of code on every page) outweighs the
 * payoff. The shape (`{ result: { data: ... } }`) is the tRPC v11 wire
 * format.
 */
async function fetchNewsPage(args: {
  slug?: string;
  cursor?: number;
  limit: number;
  locale: string;
  apiBaseUrl?: string;
  feedMode: "curated" | "verified";
}): Promise<NewsListOutput> {
  const { slug, cursor, limit, locale, apiBaseUrl, feedMode } = args;
  const inputObj: Record<string, unknown> = { limit };
  if (feedMode === "curated") {
    if (slug) inputObj.slug = slug;
    if (typeof cursor === "number") inputObj.cursor = cursor;
  }
  // tRPC v11 with superjson transformer expects `{ json: <input> }`.
  const encoded = encodeURIComponent(JSON.stringify({ json: inputObj }));
  const procedure = feedMode === "verified" ? "news.verifiedList" : "news.list";
  const url = `${buildApiUrl(apiBaseUrl, procedure)}?input=${encoded}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { "x-locale": locale, accept: "application/json" },
    credentials: "omit",
  });
  if (!res.ok) {
    throw new Error(`news.list failed: ${res.status}`);
  }
  // Response shape with superjson transformer:
  //   { result: { data: { json: <output> } } }
  const json = (await res.json()) as {
    result?: {
      data?: { json?: NewsListOutput | VerifiedNewsItem[] } | NewsListOutput | VerifiedNewsItem[];
    };
  };
  const inner = json.result?.data;
  const payload =
    (inner && typeof inner === "object" && "json" in inner
      ? (inner as { json: NewsListOutput | VerifiedNewsItem[] }).json
      : (inner as NewsListOutput | VerifiedNewsItem[] | undefined)) ?? undefined;
  if (!payload) throw new Error(`${procedure}: malformed response`);
  const data: NewsListOutput = Array.isArray(payload)
    ? {
        items: payload.map((item) => ({ ...item, outbreakId: null })),
        nextCursor: null,
      }
    : payload;
  // `publishedAt` arrives as ISO string over the wire; normalize to Date
  // so the consumer can format it without a guard.
  return {
    nextCursor: data.nextCursor,
    items: data.items.map((item) => ({
      ...item,
      publishedAt: new Date(item.publishedAt as unknown as string),
      verifiedAt: item.verifiedAt ? new Date(item.verifiedAt as unknown as string) : null,
    })),
  };
}

export function NewsFeed({
  slug,
  feedMode = "curated",
  locale,
  t,
  limit = 20,
  initialItems,
  apiBaseUrl,
}: NewsFeedProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const query = useInfiniteQuery<NewsListOutput, Error>({
    queryKey: ["news.list", { slug, locale, limit, feedMode }],
    queryFn: ({ pageParam }) =>
      fetchNewsPage({
        slug,
        cursor: pageParam as number | undefined,
        limit,
        locale,
        apiBaseUrl,
        feedMode,
      }),
    getNextPageParam: (last) =>
      feedMode === "curated" ? (last.nextCursor ?? undefined) : undefined,
    initialPageParam: undefined,
    initialData: initialItems
      ? {
          pages: [initialItems],
          pageParams: [undefined],
        }
      : undefined,
    staleTime: 30_000,
  });

  // IntersectionObserver — fetch next page when the sentinel enters view.
  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && query.hasNextPage && !query.isFetchingNextPage) {
            void query.fetchNextPage();
          }
        }
      },
      { rootMargin: "200px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const items: NewsItem[] = query.data?.pages.flatMap((p) => p.items) ?? [];
  const isInitialLoading = query.isLoading && items.length === 0;
  const isEmpty = !isInitialLoading && items.length === 0;

  return (
    <section
      aria-label={t("news.ariaLabel")}
      className="news-feed"
      style={{ display: "grid", gap: "0.75rem" }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.5rem",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: "var(--font-sans)",
            fontSize: "1.125rem",
            fontWeight: 600,
            color: "var(--color-fg-default)",
          }}
        >
          {t("news.title")}
        </h2>
        {query.isFetching && !isInitialLoading ? (
          <span
            aria-live="polite"
            style={{
              fontSize: "0.75rem",
              fontFamily: "var(--font-mono)",
              color: "var(--color-fg-muted)",
            }}
          >
            {t("news.refreshing")}
          </span>
        ) : null}
      </header>

      {isInitialLoading ? <NewsFeedSkeleton t={t} /> : null}

      {isEmpty ? (
        <p
          className="news-feed-empty"
          style={{
            margin: 0,
            padding: "1.5rem 1rem",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: "0.5rem",
            background: "var(--color-bg-surface)",
            color: "var(--color-fg-muted)",
            fontSize: "0.875rem",
            textAlign: "center",
          }}
        >
          {t("news.empty")}
        </p>
      ) : null}

      {items.map((item) => (
        <NewsCard key={item.id} item={item} locale={locale} t={t} />
      ))}

      {/* Sentinel for IntersectionObserver — stays in the DOM whether
          or not we have a next page so the observer reattaches cleanly
          on hot-reload. */}
      <div
        ref={sentinelRef}
        data-testid="news-feed-sentinel"
        aria-hidden="true"
        style={{ height: "1px", marginBlockStart: "0.5rem" }}
      />

      {query.isFetchingNextPage ? (
        <p
          aria-live="polite"
          style={{
            margin: 0,
            paddingBlock: "0.75rem",
            textAlign: "center",
            fontSize: "0.8125rem",
            color: "var(--color-fg-muted)",
          }}
        >
          {t("news.loadingMore")}
        </p>
      ) : null}

      {query.isError ? (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: "0.75rem 1rem",
            border: "1px solid var(--color-accent-emergency)",
            background: "hsl(354 78% 52% / 0.08)",
            color: "var(--color-accent-emergency)",
            borderRadius: "0.375rem",
            fontSize: "0.875rem",
          }}
        >
          {t("news.error")}
        </p>
      ) : null}
    </section>
  );
}

function NewsCard({
  item,
  locale,
  t,
}: {
  item: NewsItem;
  locale: string;
  t: TranslateFn;
}) {
  const colour = SEVERITY_TOKEN[item.severity] ?? SEVERITY_TOKEN.info;
  const absoluteDate = formatDate(item.publishedAt, locale, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  return (
    <article
      className="news-card"
      style={{
        display: "grid",
        gap: "0.375rem",
        padding: "0.875rem 1rem",
        background: "var(--color-bg-surface)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: "0.5rem",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          fontSize: "0.75rem",
          fontFamily: "var(--font-mono)",
          color: "var(--color-fg-muted)",
        }}
      >
        {/* Severity dot — status indicator; rounded-full is allowed per
            uncodixify-ui rule for status dots. */}
        <span
          aria-label={t(`news.severity.${item.severity}`)}
          role="img"
          style={{
            display: "inline-block",
            inlineSize: "0.5rem",
            blockSize: "0.5rem",
            borderRadius: "50%",
            background: colour,
            flex: "0 0 auto",
          }}
        />
        <SourceCitation
          sources={
            item.verifiedAt
              ? [
                  {
                    url: item.sourceUrl,
                    label: item.source,
                    verifiedAt: (item.verifiedAt as Date).toISOString(),
                  } satisfies Source,
                ]
              : []
          }
          locale={locale}
          t={t}
          siblingGlyph={true}
        >
          <span
            style={{
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "var(--color-fg-default)",
              fontWeight: 600,
            }}
          >
            {item.source}
          </span>
        </SourceCitation>
        <span aria-hidden="true">·</span>
        <time
          dateTime={
            item.publishedAt instanceof Date
              ? item.publishedAt.toISOString()
              : String(item.publishedAt)
          }
          title={absoluteDate}
        >
          {formatRelative(item.publishedAt, locale)}
        </time>
      </div>

      <h3
        style={{
          margin: 0,
          fontFamily: "var(--font-sans)",
          fontSize: "0.9375rem",
          fontWeight: 600,
          lineHeight: 1.3,
          color: "var(--color-fg-default)",
        }}
      >
        <a
          href={item.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "none" }}
        >
          {item.headline}
        </a>
      </h3>

      {item.summary ? (
        <p
          style={{
            margin: 0,
            fontSize: "0.8125rem",
            lineHeight: 1.45,
            color: "var(--color-fg-muted)",
            ...clampStyle(3),
          }}
        >
          {item.summary}
        </p>
      ) : null}

      <a
        href={item.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "inline-flex",
          minInlineSize: 44,
          minBlockSize: 44,
          alignItems: "center",
          fontSize: "0.75rem",
          fontFamily: "var(--font-mono)",
          color: "var(--color-accent-info)",
          textDecoration: "underline",
          marginBlockStart: "0.125rem",
          inlineSize: "fit-content",
        }}
      >
        {t("news.readSource")}
      </a>
    </article>
  );
}

function NewsFeedSkeleton({ t }: { t: TranslateFn }) {
  // 3 placeholders with `animate-pulse` — explicitly allowed on Skeleton
  // by `.claude/rules/uncodixify-ui.md`.
  return (
    <output
      aria-label={t("news.loading")}
      data-testid="news-feed-skeleton"
      style={{ display: "grid", gap: "0.75rem" }}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="animate-pulse" /* uncodixify-ok: skeleton grid row placeholder */
          aria-hidden="true"
          style={{
            display: "grid",
            gap: "0.5rem",
            padding: "0.875rem 1rem",
            background: "var(--color-bg-surface)",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: "0.5rem",
          }}
        >
          <div
            style={{
              inlineSize: "40%",
              blockSize: "0.625rem",
              background: "var(--color-bg-surface-elevated)",
              borderRadius: "0.25rem",
            }}
          />
          <div
            style={{
              inlineSize: "85%",
              blockSize: "0.875rem",
              background: "var(--color-bg-surface-elevated)",
              borderRadius: "0.25rem",
            }}
          />
          <div
            style={{
              inlineSize: "70%",
              blockSize: "0.625rem",
              background: "var(--color-bg-surface-elevated)",
              borderRadius: "0.25rem",
            }}
          />
        </div>
      ))}
    </output>
  );
}
